import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { MeetingNotification } from '../interfaces/interface';
import { NotificationService } from '../services/NotificationService';
import { logger } from 'src/logger';

const MAX_DISPLAY = 20;

interface UseNotificationsProps {
  currentUserAlias: string;
  /** When set, only show notifications for this meeting (shared link mode). */
  meetingId?: string | null;
}

export const useNotifications = ({ currentUserAlias, meetingId }: UseNotificationsProps) => {
  const [notifications, setNotifications] = useState<Record<string, MeetingNotification[]>>({});
  const [isLoading, setIsLoading] = useState(false);
  const inFlightRef = useRef(false);

  /** Load all notifications on mount. */
  const loadNotifications = useCallback(async () => {
    if (!currentUserAlias || inFlightRef.current) return;
    inFlightRef.current = true;
    setIsLoading(true);
    try {
      const result = await NotificationService.getNotifications(currentUserAlias);
      setNotifications(result);
    } catch (error) {
      logger.error('Failed to load notifications', { error });
    } finally {
      setIsLoading(false);
      inFlightRef.current = false;
    }
  }, [currentUserAlias]);

  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  /** Notifications filtered by meetingId if set, otherwise all. */
  const filteredNotifications = useMemo(() => {
    if (!meetingId) return notifications;
    const scoped = notifications[meetingId];
    return scoped ? { [meetingId]: scoped } : {};
  }, [notifications, meetingId]);

  /** All notifications flattened, sorted newest first, capped at MAX_DISPLAY. */
  const allNotifications: MeetingNotification[] = useMemo(
    () =>
      Object.values(filteredNotifications)
        .flat()
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
        .slice(0, MAX_DISPLAY),
    [filteredNotifications]
  );

  /** Unread count derived from data. */
  const unreadCount = useMemo(
    () =>
      Object.values(filteredNotifications)
        .flat()
        .filter((n) => !n.isRead).length,
    [filteredNotifications]
  );

  const markAsRead = useCallback(
    async (meetingId: string, notificationId: string) => {
      setNotifications((prev) => {
        const meetingNotifs = prev[meetingId];
        if (!meetingNotifs) return prev;
        return { ...prev, [meetingId]: meetingNotifs.map((n) => (n.id === notificationId ? { ...n, isRead: true } : n)) };
      });
      try {
        await NotificationService.markAsRead(currentUserAlias, meetingId, notificationId);
      } catch (error) {
        logger.error('Failed to mark notification as read', { error, notificationId });
        loadNotifications();
      }
    },
    [currentUserAlias, loadNotifications]
  );

  const markAllAsRead = useCallback(async () => {
    setNotifications((prev) => {
      const updated: Record<string, MeetingNotification[]> = {};
      for (const [mid, notifs] of Object.entries(prev)) {
        updated[mid] = notifs.map((n) => ({ ...n, isRead: true }));
      }
      return updated;
    });
    try {
      await NotificationService.markAllAsRead(currentUserAlias);
    } catch (error) {
      logger.error('Failed to mark all as read', { error });
      loadNotifications();
    }
  }, [currentUserAlias, loadNotifications]);

  const dismissNotification = useCallback(
    async (meetingId: string, notificationId: string) => {
      setNotifications((prev) => {
        const meetingNotifs = prev[meetingId];
        if (!meetingNotifs) return prev;
        const filtered = meetingNotifs.filter((n) => n.id !== notificationId);
        if (filtered.length === 0) {
          const { [meetingId]: _, ...rest } = prev;
          return rest;
        }
        return { ...prev, [meetingId]: filtered };
      });
      try {
        await NotificationService.dismissNotification(currentUserAlias, meetingId, notificationId);
      } catch (error) {
        logger.error('Failed to dismiss notification', { error, notificationId });
        loadNotifications();
      }
    },
    [currentUserAlias, loadNotifications]
  );

  const refresh = useCallback(async () => {
    await loadNotifications();
  }, [loadNotifications]);

  return {
    unreadCount,
    allNotifications,
    notifications,
    isLoading,
    loadNotifications,
    markAsRead,
    markAllAsRead,
    dismissNotification,
    refresh
  };
};
